import api from './Api';
import {
    ICartaoBanco,
    IContaBancaria,
    IComprovantesVisao,
    IFolhaLinhaOpcao,
    IFolhaVinculoItem,
    IFolhaVisao,
    INfsePolitica,
    INfseVisao,
    IPagamentosRfbVisao,
    IRetornoProvisaoOpcao,
    IRetornosVisao,
    IExtratoModulo,
    IFechamentosVisao,
    IFiltroLista,
    IFiltroMinutas,
    IFolhaEmpregado,
    IOpcoesLimitadas,
    IResumoBancario,
    IIndicadoresVisao,
    IMinutaBancaria,
    IPoliticaAutoaprovacao,
    IMinutaLoteResumo,
    IMinutasVisao,
    IModuloBancarioVisao,
    IPlanoContaOpcao,
    ISalvarContaBancaria,
    ISetupCategoria,
    ISetupVersao,
    ISugestaoContaBancaria,
    ITrilhaPagina,
    ITrilhaRelatorio,
} from '../interfaces/IModuloBancario';

const salvarArquivo = (conteudo: Blob, nome: string) => {
    const url = window.URL.createObjectURL(conteudo);
    const link = document.createElement('a');
    link.href = url;
    link.download = nome;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

// DEVN3-767 — Módulo Bancário. O upload do arquivo para o storage usa o mesmo
// ArquivoOperacaoService.gerarUrl/enviarParaStorage do modal do cartão.
const ModuloBancarioService = {
    listar: async (esteiraId: number, ano?: number, mes?: number, filtro?: IFiltroLista): Promise<IModuloBancarioVisao> => {
        const { data } = await api.get<IModuloBancarioVisao>(`ModuloBancario/${esteiraId}`, {
            params: { ...(ano && mes ? { ano, mes } : {}), ...filtro },
        });
        return data;
    },

    resumo: async (esteiraId: number, ano?: number, mes?: number): Promise<IResumoBancario> => {
        const { data } = await api.get<IResumoBancario>(`ModuloBancario/${esteiraId}/resumo`, {
            params: ano && mes ? { ano, mes } : {},
        });
        return data;
    },

    cartoesBanco: async (esteiraId: number): Promise<ICartaoBanco[]> => {
        const { data } = await api.get<ICartaoBanco[]>(`ModuloBancario/${esteiraId}/cartoes-banco`);
        return data;
    },

    criarConta: async (request: ISalvarContaBancaria): Promise<IContaBancaria> => {
        const { data } = await api.post<IContaBancaria>('ModuloBancario/contas', request);
        return data;
    },

    atualizarConta: async (contaId: number, request: ISalvarContaBancaria): Promise<IContaBancaria> => {
        const { data } = await api.put<IContaBancaria>(`ModuloBancario/contas/${contaId}`, request);
        return data;
    },

    registrarExtrato: async (esteiraId: number, nomeGerado: string, nomeOriginal: string, contaBancariaId?: number): Promise<IExtratoModulo> => {
        const { data } = await api.post<IExtratoModulo>('ModuloBancario/extratos', {
            esteiraId,
            nomeGerado,
            nomeOriginal,
            ...(contaBancariaId ? { contaBancariaId } : {}),
        });
        return data;
    },

    vincularConta: async (arquivoId: number, contaBancariaId: number): Promise<void> => {
        await api.post(`ModuloBancario/extratos/${arquivoId}/conta`, { contaBancariaId });
    },

    informarSaldos: async (arquivoId: number, saldoInicial: number, saldoFinal: number): Promise<void> => {
        await api.post(`ModuloBancario/extratos/${arquivoId}/saldos`, { saldoInicial, saldoFinal });
    },

    descartar: async (arquivoId: number): Promise<void> => {
        await api.post(`ModuloBancario/extratos/${arquivoId}/descartar`);
    },

    sugestao: async (arquivoId: number): Promise<ISugestaoContaBancaria | null> => {
        const { data } = await api.get<ISugestaoContaBancaria | null>(`ModuloBancario/extratos/${arquivoId}/sugestao`);
        return data || null;
    },

    aceitarSugestao: async (arquivoId: number, request: ISalvarContaBancaria): Promise<IContaBancaria> => {
        const { data } = await api.post<IContaBancaria>(`ModuloBancario/extratos/${arquivoId}/sugestao`, request);
        return data;
    },

    iniciar: async (esteiraId: number): Promise<string> => {
        const { data } = await api.post<{ message: string }>(`ModuloBancario/${esteiraId}/iniciar`);
        return data.message;
    },

    // ---- DEVN3-768: Setup bancário ----------------------------------------------------------------

    setup: async (esteiraId: number): Promise<ISetupCategoria[]> => {
        const { data } = await api.get<ISetupCategoria[]>(`ModuloBancario/${esteiraId}/setup`);
        return data;
    },

    planoContas: async (esteiraId: number): Promise<IPlanoContaOpcao[]> => {
        const { data } = await api.get<IPlanoContaOpcao[]>(`ModuloBancario/${esteiraId}/plano-contas`);
        return data;
    },

    criarCategoria: async (esteiraId: number, request: { nome: string; direcao: string; contaContabil: string; padroes: string[]; vigenciaInicio?: string | null }): Promise<ISetupCategoria> => {
        const { data } = await api.post<ISetupCategoria>(`ModuloBancario/${esteiraId}/setup/categorias`, request);
        return data;
    },

    versoesCategoria: async (esteiraId: number, categoriaId: number): Promise<ISetupVersao[]> => {
        const { data } = await api.get<ISetupVersao[]>(`ModuloBancario/${esteiraId}/setup/categorias/${categoriaId}/versoes`);
        return data;
    },

    salvarVersao: async (esteiraId: number, categoriaId: number, request: { contaContabil: string; padroes: string[]; vigenciaInicio?: string | null }): Promise<ISetupCategoria> => {
        const { data } = await api.post<ISetupCategoria>(`ModuloBancario/${esteiraId}/setup/categorias/${categoriaId}/versoes`, request);
        return data;
    },

    salvarExcecao: async (esteiraId: number, categoriaId: number, request: { contaBancariaId: number; contaContabil: string; vigenciaInicio?: string | null }): Promise<ISetupCategoria> => {
        const { data } = await api.post<ISetupCategoria>(`ModuloBancario/${esteiraId}/setup/categorias/${categoriaId}/excecoes`, request);
        return data;
    },

    /** contaBancariaId > 0 encerra (ou reabre) a exceção dessa conta bancária. */
    alterarSituacao: async (esteiraId: number, categoriaId: number, ativa: boolean, contaBancariaId = 0): Promise<ISetupCategoria> => {
        const { data } = await api.post<ISetupCategoria>(
            `ModuloBancario/${esteiraId}/setup/categorias/${categoriaId}/${ativa ? 'reativar' : 'inativar'}`,
            { contaBancariaId });
        return data;
    },

    renomearCategoria: async (esteiraId: number, categoriaId: number, nome: string): Promise<ISetupCategoria> => {
        const { data } = await api.put<ISetupCategoria>(`ModuloBancario/${esteiraId}/setup/categorias/${categoriaId}/nome`, { nome });
        return data;
    },

    // ---- DEVN3-769: geração de minutas ------------------------------------------------------------

    minutas: async (esteiraId: number, ano: number, mes: number, filtro?: IFiltroMinutas): Promise<IMinutasVisao> => {
        const { data } = await api.get<IMinutasVisao>(`ModuloBancario/${esteiraId}/minutas`, { params: { ano, mes, ...filtro } });
        return data;
    },

    gerarMinutas: async (esteiraId: number, ano: number, mes: number): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/minutas/gerar`, { ano, mes });
    },

    escolherCandidato: async (esteiraId: number, minutaId: number, opcao: number): Promise<IMinutaBancaria> => {
        const { data } = await api.post<IMinutaBancaria>(`ModuloBancario/${esteiraId}/minutas/${minutaId}/escolher`, { opcao });
        return data;
    },

    // ---- DEVN3-770: aprovação de minutas e trilha -------------------------------------------------

    previaLote: async (esteiraId: number, ano: number, mes: number, minutaIds: number[]): Promise<IMinutaLoteResumo> => {
        const { data } = await api.post<IMinutaLoteResumo>(`ModuloBancario/${esteiraId}/minutas/previa-lote`, { ano, mes, minutaIds });
        return data;
    },

    aprovarLote: async (esteiraId: number, ano: number, mes: number, minutaIds: number[], ciencia: boolean): Promise<IMinutaLoteResumo> => {
        const { data } = await api.post<IMinutaLoteResumo>(`ModuloBancario/${esteiraId}/minutas/aprovar-lote`, { ano, mes, minutaIds, ciencia });
        return data;
    },

    aprovarMinuta: async (esteiraId: number, minutaId: number): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/minutas/${minutaId}/aprovar`);
    },

    reprovarMinuta: async (esteiraId: number, minutaId: number, motivo: string): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/minutas/${minutaId}/reprovar`, { motivo });
    },

    desfazerReprovacao: async (esteiraId: number, minutaId: number): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/minutas/${minutaId}/desfazer-reprovacao`);
    },

    desaprovarMinuta: async (esteiraId: number, minutaId: number): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/minutas/${minutaId}/desaprovar`);
    },

    revisarMinuta: async (esteiraId: number, minutaId: number, resultado: string, motivo?: string): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/minutas/${minutaId}/revisao`, { resultado, motivo });
    },

    indicadores: async (esteiraId: number, ano?: number, mes?: number): Promise<IIndicadoresVisao> => {
        const { data } = await api.get(`ModuloBancario/${esteiraId}/indicadores`, { params: { ano, mes } });
        return data;
    },

    politicaAutoaprovacao: async (esteiraId: number): Promise<IPoliticaAutoaprovacao> => {
        const { data } = await api.get<IPoliticaAutoaprovacao>(`ModuloBancario/${esteiraId}/politica-autoaprovacao`);
        return data;
    },

    alterarPoliticaAutoaprovacao: async (esteiraId: number, ligada: boolean): Promise<IPoliticaAutoaprovacao> => {
        const { data } = await api.put<IPoliticaAutoaprovacao>(`ModuloBancario/${esteiraId}/politica-autoaprovacao`, { ligada });
        return data;
    },

    situacaoMinutas: async (esteiraId: number, ano: number, mes: number): Promise<{ geracaoEmAndamento: boolean; ultimaOperacaoId: number | null }> => {
        const { data } = await api.get(`ModuloBancario/${esteiraId}/minutas/situacao`, { params: { ano, mes } });
        return data;
    },

    trilha: async (esteiraId: number, filtros: { contaBancariaId?: number; competencia?: string; acao?: string; pagina: number }): Promise<ITrilhaPagina> => {
        const { data } = await api.get<ITrilhaPagina>(`ModuloBancario/${esteiraId}/trilha`, { params: filtros });
        return data;
    },

    // ---- DEVN3-779: relatório da trilha em PDF -----------------------------------------------------

    solicitarTrilhaPdf: async (esteiraId: number, contaBancariaId: number, competencia: string): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/trilha/pdf`, { contaBancariaId, competencia });
    },

    relatoriosTrilha: async (esteiraId: number, contaBancariaId: number, competencia: string): Promise<ITrilhaRelatorio[]> => {
        const { data } = await api.get<ITrilhaRelatorio[]>(`ModuloBancario/${esteiraId}/trilha/relatorios`, { params: { contaBancariaId, competencia } });
        return data;
    },

    baixarRelatorioTrilha: async (esteiraId: number, relatorioId: number, nome: string): Promise<void> => {
        const { data } = await api.get<Blob>(`ModuloBancario/${esteiraId}/trilha/relatorios/${relatorioId}/arquivo`, { responseType: 'blob' });
        salvarArquivo(data, nome);
    },

    // ---- DEVN3-771: fechamento e exportação ---------------------------------------------------------

    fechamentos: async (esteiraId: number, ano: number, mes: number): Promise<IFechamentosVisao> => {
        const { data } = await api.get<IFechamentosVisao>(`ModuloBancario/${esteiraId}/fechamentos`, { params: { ano, mes } });
        return data;
    },

    fecharConta: async (esteiraId: number, contaId: number, ano: number, mes: number): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/fechamentos/${contaId}/fechar`, { ano, mes });
    },

    desfazerFechamento: async (esteiraId: number, contaId: number, ano: number, mes: number): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/fechamentos/${contaId}/desfazer`, { ano, mes });
    },

    exportarFechamento: async (esteiraId: number, contaId: number, ano: number, mes: number): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/fechamentos/${contaId}/exportar`, { ano, mes });
    },

    reemitirFechamento: async (esteiraId: number, contaId: number, ano: number, mes: number, justificativa: string): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/fechamentos/${contaId}/reemitir`, { ano, mes, justificativa });
    },

    reabrirFechamento: async (esteiraId: number, contaId: number, ano: number, mes: number, motivo: string): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/fechamentos/${contaId}/reabrir`, { ano, mes, motivo });
    },

    informarRetorno: async (esteiraId: number, contaId: number, ano: number, mes: number, retorno: string, criticas: string): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/fechamentos/${contaId}/retorno`, { ano, mes, retorno, criticas });
    },

    baixarVersao: async (esteiraId: number, versaoId: number, nome: string): Promise<void> => {
        const { data } = await api.get<Blob>(`ModuloBancario/${esteiraId}/fechamentos/versoes/${versaoId}/arquivo`, { responseType: 'blob' });
        salvarArquivo(data, nome);
    },

    baixarTrilhaCsv: async (esteiraId: number, filtros: { contaBancariaId?: number; competencia?: string }, nome: string): Promise<void> => {
        const { data } = await api.get<Blob>(`ModuloBancario/${esteiraId}/trilha/csv`, { params: filtros, responseType: 'blob' });
        salvarArquivo(data, nome);
    },

    // ---- DEVN3-772: comprovantes ---------------------------------------------------------------------

    comprovantes: async (esteiraId: number, ano: number, mes: number, filtro?: IFiltroLista): Promise<IComprovantesVisao> => {
        const { data } = await api.get<IComprovantesVisao>(`ModuloBancario/${esteiraId}/comprovantes`, { params: { ano, mes, ...filtro } });
        return data;
    },

    registrarComprovante: async (esteiraId: number, request: { ano: number; mes: number; nomeGerado: string; nomeOriginal: string }): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/comprovantes`, request);
    },

    decidirComprovantes: async (esteiraId: number, arquivoIds: number[], processar: boolean): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/comprovantes/decidir`, { arquivoIds, processar });
    },

    dispensarComprovantes: async (esteiraId: number, request: { ano: number; mes: number; justificativa: string; ciencia: boolean }): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/comprovantes/dispensar`, request);
    },

    excluirComprovante: async (esteiraId: number, comprovanteId: number): Promise<void> => {
        await api.delete(`ModuloBancario/${esteiraId}/comprovantes/${comprovanteId}`);
    },

    baixarComprovante: async (esteiraId: number, arquivoId: number, nome: string): Promise<void> => {
        const { data } = await api.get<Blob>(`ModuloBancario/${esteiraId}/comprovantes/arquivos/${arquivoId}`, { responseType: 'blob' });
        salvarArquivo(data, nome);
    },

    anexarComprovante: async (esteiraId: number, minutaId: number, comprovanteId: number): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/minutas/${minutaId}/comprovante`, { comprovanteId });
    },

    desanexarComprovante: async (esteiraId: number, minutaId: number): Promise<void> => {
        await api.delete(`ModuloBancario/${esteiraId}/minutas/${minutaId}/comprovante`);
    },

    // ---- DEVN3-774: retorno de cobrança ----------------------------------------------------------------

    retornos: async (esteiraId: number, ano: number, mes: number, filtro?: IFiltroLista): Promise<IRetornosVisao> => {
        const { data } = await api.get<IRetornosVisao>(`ModuloBancario/${esteiraId}/retornos`, { params: { ano, mes, ...filtro } });
        return data;
    },

    registrarRetorno: async (esteiraId: number, request: { ano: number; mes: number; nomeGerado: string; nomeOriginal: string; contaBancariaId: number | null }): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/retornos`, request);
    },

    decidirRetornos: async (esteiraId: number, arquivoIds: number[], processar: boolean): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/retornos/decidir`, { arquivoIds, processar });
    },

    dispensarRetornos: async (esteiraId: number, request: { ano: number; mes: number; justificativa: string; ciencia: boolean }): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/retornos/dispensar`, request);
    },

    escolherContaRetorno: async (esteiraId: number, arquivoId: number, contaBancariaId: number): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/retornos/arquivos/${arquivoId}/conta`, { contaBancariaId });
    },

    baixarRetorno: async (esteiraId: number, arquivoId: number, nome: string): Promise<void> => {
        const { data } = await api.get<Blob>(`ModuloBancario/${esteiraId}/retornos/arquivos/${arquivoId}`, { responseType: 'blob' });
        salvarArquivo(data, nome);
    },

    provisoesDoTitulo: async (esteiraId: number, tituloId: number, busca?: string): Promise<IOpcoesLimitadas<IRetornoProvisaoOpcao>> => {
        const { data } = await api.get<IOpcoesLimitadas<IRetornoProvisaoOpcao>>(`ModuloBancario/${esteiraId}/retornos/titulos/${tituloId}/provisoes`, { params: { busca: busca || undefined } });
        return data;
    },

    vincularTitulo: async (esteiraId: number, tituloId: number, lancamentoId: number | null): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/retornos/titulos/${tituloId}/provisao`, { lancamentoId });
    },

    // ---- DEVN3-775: pagamentos RFB --------------------------------------------------------------------

    pagamentosRfb: async (esteiraId: number, ano: number, mes: number, filtro?: IFiltroLista): Promise<IPagamentosRfbVisao> => {
        const { data } = await api.get<IPagamentosRfbVisao>(`ModuloBancario/${esteiraId}/pagamentos-rfb`, { params: { ano, mes, ...filtro } });
        return data;
    },

    registrarPagamentoRfb: async (esteiraId: number, request: { ano: number; mes: number; nomeGerado: string; nomeOriginal: string }): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/pagamentos-rfb`, request);
    },

    decidirPagamentosRfb: async (esteiraId: number, arquivoIds: number[], processar: boolean): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/pagamentos-rfb/decidir`, { arquivoIds, processar });
    },

    dispensarPagamentosRfb: async (esteiraId: number, request: { ano: number; mes: number; justificativa: string; ciencia: boolean }): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/pagamentos-rfb/dispensar`, request);
    },

    salvarContaCodigoRfb: async (esteiraId: number, request: { codigoReceita: string; contaContabil: string; ativa: boolean }): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/pagamentos-rfb/conta-codigo`, request);
    },

    baixarPagamentoRfb: async (esteiraId: number, arquivoId: number, nome: string): Promise<void> => {
        const { data } = await api.get<Blob>(`ModuloBancario/${esteiraId}/pagamentos-rfb/arquivos/${arquivoId}`, { responseType: 'blob' });
        salvarArquivo(data, nome);
    },

    folha: async (esteiraId: number, ano: number, mes: number, filtro?: IFiltroLista): Promise<IFolhaVisao> => {
        const { data } = await api.get<IFolhaVisao>(`ModuloBancario/${esteiraId}/folha`, { params: { ano, mes, ...filtro } });
        return data;
    },

    empregadosDaFolha: async (esteiraId: number, ano: number, mes: number): Promise<IFolhaEmpregado[]> => {
        const { data } = await api.get<IFolhaEmpregado[]>(`ModuloBancario/${esteiraId}/folha/empregados`, { params: { ano, mes } });
        return data;
    },

    registrarFolha: async (esteiraId: number, request: { ano: number; mes: number; nomeGerado: string; nomeOriginal: string }): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/folha`, request);
    },

    descartarFolha: async (esteiraId: number, arquivoIds: number[]): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/folha/descartar`, { arquivoIds });
    },

    baixarFolha: async (esteiraId: number, arquivoId: number, nome: string): Promise<void> => {
        const { data } = await api.get<Blob>(`ModuloBancario/${esteiraId}/folha/arquivos/${arquivoId}`, { responseType: 'blob' });
        salvarArquivo(data, nome);
    },

    salvarCalendarioFolha: async (esteiraId: number, request: { tipoFolha: string; regra: string; vigenciaInicio: string | null }): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/folha/calendario`, request);
    },

    linhasDaFolha: async (esteiraId: number, ano: number, mes: number, busca?: string): Promise<IOpcoesLimitadas<IFolhaLinhaOpcao>> => {
        const { data } = await api.get<IOpcoesLimitadas<IFolhaLinhaOpcao>>(`ModuloBancario/${esteiraId}/folha/linhas`, { params: { ano, mes, busca: busca || undefined } });
        return data;
    },

    vincularFolha: async (esteiraId: number, extratoId: number, itens: IFolhaVinculoItem[]): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/folha/linhas/${extratoId}/vinculo`, { itens });
    },

    nfse: async (esteiraId: number, ano: number, mes: number, filtro?: IFiltroLista): Promise<INfseVisao> => {
        const { data } = await api.get<INfseVisao>(`ModuloBancario/${esteiraId}/nfse`, { params: { ano, mes, ...filtro } });
        return data;
    },

    registrarNfse: async (esteiraId: number, request: { ano: number; mes: number; nomeGerado: string; nomeOriginal: string }): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/nfse`, request);
    },

    descartarNfse: async (esteiraId: number, arquivoIds: number[]): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/nfse/descartar`, { arquivoIds });
    },

    baixarNfse: async (esteiraId: number, arquivoId: number, nome: string): Promise<void> => {
        const { data } = await api.get<Blob>(`ModuloBancario/${esteiraId}/nfse/arquivos/${arquivoId}`, { responseType: 'blob' });
        salvarArquivo(data, nome);
    },

    provisoesDaNota: async (esteiraId: number, notaId: number, busca?: string): Promise<IOpcoesLimitadas<IRetornoProvisaoOpcao>> => {
        const { data } = await api.get<IOpcoesLimitadas<IRetornoProvisaoOpcao>>(`ModuloBancario/${esteiraId}/nfse/notas/${notaId}/provisoes`, { params: { busca: busca || undefined } });
        return data;
    },

    vincularNota: async (esteiraId: number, notaId: number, lancamentoId: number | null): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/nfse/notas/${notaId}/provisao`, { lancamentoId });
    },

    politicaNfse: async (esteiraId: number): Promise<INfsePolitica> => {
        const { data } = await api.get<INfsePolitica>(`ModuloBancario/${esteiraId}/nfse/politica`);
        return data;
    },

    salvarPoliticaNfse: async (esteiraId: number, provisaoPeloLiquido: boolean): Promise<INfsePolitica> => {
        const { data } = await api.post<INfsePolitica>(`ModuloBancario/${esteiraId}/nfse/politica`, { provisaoPeloLiquido });
        return data;
    },

    /** automatico: só enfileira se a esteira ativa tem o que conferir. */
    conferirRazao: async (esteiraId: number, automatico: boolean): Promise<void> => {
        await api.post(`ModuloBancario/${esteiraId}/conferir-razao`, null, { params: { automatico } });
    },
};

export default ModuloBancarioService;
